import { useState } from "react";
import {
  Platform,
  Pressable,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type MoonTabOption<Id extends string = string> = {
  id: Id;
  label: string;
  icon?: LucideIcon;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
};

type Props<Id extends string> = {
  options: MoonTabOption<Id>[];
  active: Id;
  onChange: (id: Id) => void;
  style?: StyleProp<ViewStyle>;
};

/** Connected tabs that form the top edge of a Moon card. */
export function MoonTabs<Id extends string>({
  options,
  active,
  onChange,
  style,
}: Props<Id>) {
  const [hoveredTab, setHoveredTab] = useState<Id | null>(null);
  const capturePress = usePressCapture("MoonTabs");

  return (
    <View
      style={[
        {
          alignItems: "center",
          zIndex: 30,
          elevation: 30,
        },
        style,
      ]}
    >
      <View style={{ width: "100%", flexDirection: "row", gap: 8 }}>
        {options.map((option) => {
          const selected = option.id === active;
          const hovered = hoveredTab === option.id;
          const OptionIcon = option.icon;
          const color = selected ? EMBER.colors.text : EMBER.colors.textLight;

          return (
            <Pressable
              key={option.id}
              onPress={() => {
                capturePress(
                  pressLabel(option.label),
                  option.action ?? option.id,
                );
                onChange(option.id);
              }}
              onHoverIn={() => setHoveredTab(option.id)}
              onHoverOut={() =>
                setHoveredTab((current) =>
                  current === option.id ? null : current,
                )
              }
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              aria-selected={selected}
              style={({ pressed }) => ({
                flex: 1,
                minHeight: 48,
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "row",
                gap: 5,
                paddingHorizontal: 7,
                paddingTop: 9,
                paddingBottom: 14,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderWidth: 1,
                borderBottomWidth: 0,
                borderColor: selected
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(255, 255, 255, 0.34)",
                backgroundColor: selected
                  ? "rgba(255, 255, 255, 0.36)"
                  : "rgba(255, 255, 255, 0.12)",
                shadowColor: "#000",
                shadowOpacity: selected ? 0.045 : hovered ? 0.04 : 0.015,
                shadowRadius: selected ? 18 : hovered ? 16 : 8,
                shadowOffset: { width: 0, height: hovered ? -4 : -1 },
                transform: [{ translateY: pressed ? -2 : hovered ? -6 : 0 }],
                ...(Platform.OS === "web"
                  ? { transition: "all 180ms cubic-bezier(0.2,0,0.2,1)" }
                  : null),
              })}
            >
              {OptionIcon ? (
                <OptionIcon
                  size={13}
                  color={color}
                  strokeWidth={selected ? 2.2 : 1.8}
                />
              ) : null}
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                adjustsFontSizeToFit
                style={{
                  fontSize: 11,
                  fontWeight: selected ? "700" : "600",
                  color,
                }}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
